<script setup lang="ts">
import { PropType, ref } from "vue";
import { type Emits, type NavigationLink } from ".";
import "@maersk-global/mds-components-core/mc-icon";
import "@maersk-global/mds-components-core/mc-tag";
import "@maersk-global/mds-components-core/mc-button";
import "@maersk-global/mds-components-core/mc-tooltip";
import { translationKeyTransform } from "@/utils/translations-key-transformation";
import { IsceSavedWorkspaceList } from "@/features/workspaces";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { ROUTES } from "@/router/constants";
import { userAnalyticsService } from "@/services";
import { Modal as IsceModal } from "@/components/Modal";

const { t, locale } = useI18n();
const route = useRoute();

const getImageUrl = (imageUrl: string) => new URL(`../../assets/${imageUrl}`, import.meta.url).href;

const emit = defineEmits<Emits>();

const handleNavigationLinkClick = (link: NavigationLink) => {
  userAnalyticsService.trackEvent("Nav: Side Menu Click", {
    page: "sidebar",
    linkName: link.name,
    destination: link.route
  });
  emit("navigationLinkClick", link);
};

defineProps({
  topLinks: {
    type: Array as PropType<NavigationLink[]>,
    required: true
  },
  isNewWorkspaceButtonVisible: {
    type: Boolean,
    required: false,
    default: false
  }
});

const hasRouteParams = (link: NavigationLink) =>
  [ROUTES.RISK_MANAGEMENT, ROUTES.REPORTING, ROUTES.CARRIER_PERFORMANCE].includes(link.route);
const isLinkActive = (link: NavigationLink) => route.path.startsWith(link.route);

const isLockedModalOpen = ref(false);
const selectedLockedFeatureName = ref("");
const selectedLockedVideoUrl = ref("");

const PRODUCT_BENEFITS: Record<string, string> = {
  DASHBOARD: "DASHBOARD_BENEFIT",
  SHIPMENTS: "SHIPMENTS_BENEFIT",
  AIR_SHIPMENTS: "AIR_SHIPMENTS_BENEFIT",
  "D&D import": "DND_IMPORT_BENEFIT",
  "D&D": "DND_BENEFIT",
  PORT_CONGESTION: "PORT_CONGESTION_BENEFIT",
  EMISSIONS: "EMISSIONS_BENEFIT",
  MAERSK_RISK_MANAGEMENT: "MAERSK_RISK_MANAGEMENT_BENEFIT",
  "CARRIER_PERFORMANCE.TITLE": "CARRIER_PERFORMANCE_BENEFIT",
  REPORTING: "REPORTING_BENEFIT"
};

const getEmbedVideoUrl = (url: string): string => {
  if (!url) return "";
  if (url.includes("youtube.com/watch")) {
    try {
      const urlObj = new URL(url);
      const videoId = urlObj.searchParams.get("v");
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    } catch {
      return url.replace("youtube.com/watch?v=", "youtube.com/embed/");
    }
  }
  if (url.includes("youtu.be/")) {
    try {
      const urlObj = new URL(url);
      const videoId = urlObj.pathname.substring(1);
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    } catch {
      return url.replace("youtu.be/", "youtube.com/embed/");
    }
  }
  return url;
};

const handleLockedClick = (link: NavigationLink) => {
  userAnalyticsService.trackEvent("Upsell: Locked Feature Clicked", {
    featureName: link.name,
    featureId: link.route
  });
  selectedLockedFeatureName.value = link.name;
  selectedLockedVideoUrl.value = link.videoUrl || "https://www.youtube.com/embed/TI4J_PLDY-w";
  isLockedModalOpen.value = true;
};
</script>
<template>
  <div class="side-navigation">
    <div
      class="side-navigation__header"
      :class="`${locale === 'ar' ? 'right-padding' : 'left-padding'}`"
      data-test="side-navigation/header"
    >
      <img :src="getImageUrl('Star.svg')" alt="'Maersk logo'" />
      <p class="mds-text--small-normal">Maersk Visibility Studio</p>
    </div>
    <div class="side-navigation__body">
      <div v-for="(link, index) in topLinks" :key="index" class="side-navigation__body__item">
        <template v-if="link.isEnabled !== false">
          <router-link
            :data-test="`side-navigation/top-link-${index}`"
            :class="[
              'side-navigation__body__item__link',
              { 'side-navigation__body__item__link--active': hasRouteParams(link) && isLinkActive(link) }
            ]"
            exact-active-class="side-navigation__body__item__link--active"
            :to="link.route"
            @click="handleNavigationLinkClick(link)"
          >
            <div
              class="side-navigation__body__item__link__content icon--black"
              :class="`${locale === 'ar' ? 'left-padding' : 'right-padding'}`"
            >
              <mc-icon :icon="link.icon"></mc-icon>
              <mc-tooltip
                :label="t(translationKeyTransform(link.name))"
                fit="small"
                width="150"
                appearance="neutral"
                position="top-right"
              >
                <span slot="trigger" class="mds-text--small-normal">{{ t(translationKeyTransform(link.name)) }}</span>
              </mc-tooltip>
            </div>
          </router-link>
        </template>
        <template v-else>
          <div
            :data-test="`side-navigation/top-link-locked-${index}`"
            class="side-navigation__body__item__link"
            @click="handleLockedClick(link)"
          >
            <div class="side-navigation__body__item__link__content icon--black locked-link-content">
              <div style="display: flex; gap: 8px; align-items: center">
                <mc-icon :icon="link.icon"></mc-icon>
                <mc-tooltip
                  :label="t(translationKeyTransform(link.name))"
                  fit="small"
                  width="150"
                  appearance="neutral"
                  position="top-right"
                >
                  <span slot="trigger" class="mds-text--small-normal">{{ t(translationKeyTransform(link.name)) }}</span>
                </mc-tooltip>
              </div>
              <mc-icon
                icon="lock"
                style="height: 16px; width: 16px; fill: currentColor; display: flex; align-items: center"
              ></mc-icon>
            </div>
          </div>
        </template>
      </div>

      <isce-saved-workspace-list></isce-saved-workspace-list>

      <div v-if="isNewWorkspaceButtonVisible" class="side-navigation__body__item button--new-workspace mt-2">
        <mc-button
          data-test="side-navigation/new-workspace-btn"
          label="New Workspace..."
          icon="plus"
          variant="plain"
          width="full-width"
          justifyitems="left"
          appearance="neutral"
          @click="emit('newWorkspaceClick')"
        ></mc-button>
      </div>
    </div>

    <IsceModal
      :is-open="isLockedModalOpen"
      :heading="`Unlock ${t(translationKeyTransform(selectedLockedFeatureName))}`"
      height="auto"
      :buttons="[{ label: 'Got it', value: 'close', primary: true }]"
      style="
        --mds_brand_appearance_primary_default_background-color: var(--mds_brand_appearance_primary_default_link-color);
        --mds_brand_appearance_primary_strong_background-color: var(--mds_brand_appearance_primary_default_link-color);
      "
      @close="isLockedModalOpen = false"
      @click="isLockedModalOpen = false"
    >
      <template #body>
        <div class="upsell-modal-body">
          <div
            class="upsell-video"
            style="
              margin-top: 4px;
              border-radius: 8px;
              overflow: hidden;
              display: flex;
              justify-content: center;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            "
          >
            <iframe
              v-if="isLockedModalOpen"
              width="100%"
              height="200"
              :src="getEmbedVideoUrl(selectedLockedVideoUrl)"
              title="Maersk Premium Features"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>

          <div class="upsell-benefit">
            <mc-icon icon="star" class="benefit-icon"></mc-icon>
            <p class="benefit-text">
              <b>{{
                PRODUCT_BENEFITS[selectedLockedFeatureName] ? t(PRODUCT_BENEFITS[selectedLockedFeatureName]) : ""
              }}</b>
            </p>
          </div>

          <div class="upsell-action">
            <p class="action-text">Interested? Get in touch with our team and we'll help you get started:</p>
            <a
              href="mailto:SVP_Support@lns.maersk.com"
              class="action-email"
              @click="
                () =>
                  userAnalyticsService.trackEvent('Upsell: Email Button Clicked', {
                    featureName: selectedLockedFeatureName
                  })
              "
            >
              <mc-icon icon="envelope" class="email-icon"></mc-icon>
              svp_support@lns.maersk.com
            </a>
          </div>
        </div>
      </template>
    </IsceModal>
  </div>
</template>
<style lang="scss" scoped>
@import "@/styles/colors_mds.scss";

.side-navigation {
  background-color: $wrapper--mds_brand_appearance_neutral_weakest_background-color;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  padding-bottom: 2rem;
  border-right: 1px solid $wrapper--mds_brand_appearance_neutral_weak_background-color;

  &__header {
    display: flex;
    align-items: center;
    min-height: 56px;
    gap: 12px;
    width: 100%;
    padding-left: 20px;
    border-bottom: 1px solid $wrapper--mds_brand_appearance_neutral_weak_background-color;

    &.right-padding {
      padding-right: 20px;
    }

    &.left-padding {
      padding-left: 20px;
    }
  }

  &__body {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 12px;
    height: calc(100% - 56px);

    &__item {
      width: 100%;
      padding: 0 12px;

      &__link {
        text-decoration: none !important;
        color: $wrapper--mds_brand_appearance_neutral_default_text-color !important;
        width: 100%;
        cursor: pointer;
        border-radius: 8px;
        max-height: 36px;

        .link-menu {
          display: none;
        }

        &:hover {
          background-color: $wrapper--mds_brand_appearance_neutral_weak_background-color;

          .link-menu {
            display: block;
          }
        }

        &--active {
          background-color: $wrapper--mds_brand_appearance_neutral_weak_background-color;
          pointer-events: none;
        }

        &__content {
          width: 100%;
          display: flex;
          padding: 8px 0 8px 8px;
          gap: 8px;
          align-items: center;

          &.right-padding {
            padding-right: 0px;
          }

          &.left-padding {
            padding-left: 0px;
          }

          &.locked-link-content {
            padding: 8px 8px 8px 8px;
            justify-content: space-between;
            gap: 0;
          }
        }

        &__workspace-content {
          width: 100%;
          display: flex;
          padding: 8px;
          gap: 8px;
          align-items: center;
          justify-content: space-between;

          .ellipsis {
            display: block;
            width: 150px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    .static-text {
      width: 100%;
      padding-left: 20px;
      margin-top: 20px;
      color: $wrapper--mds_brand_appearance_neutral_weak_text-color;
    }
  }
}

.icon--black {
  mc-icon::part(icon) {
    fill: $wrapper--mds_brand_appearance_neutral_default_text-color;
  }

  mc-tooltip {
    span {
      display: inline-block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 170px;
    }
  }
}

.button--new-workspace {
  mc-button::part(button) {
    background-color: $wrapper--mds_brand_appearance_neutral_default_background-color;
    color: $wrapper--mds_brand_appearance_neutral_weakest_text-color;
    border-radius: 8px;
  }

  mc-button::part(button):hover {
    background-color: $wrapper--mds_brand_appearance_neutral_weak_background-color;
  }

  mc-button::part(icon) {
    fill: $wrapper--mds_brand_appearance_neutral_weakest_text-color;
  }
}

mc-button::part(button) {
  padding: 0;
}

/* Premium Modal Styles */
.upsell-modal-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
}

.upsell-benefit {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: $wrapper--mds_brand_appearance_neutral_weakest_background-color;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid var(--mds_brand_appearance_primary_default_link-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  .benefit-icon {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 1px;
  }
  .benefit-icon::part(icon) {
    fill: var(--mds_brand_appearance_primary_default_link-color);
  }

  .benefit-text {
    font-size: 13px;
    color: $wrapper--mds_brand_appearance_neutral_default_text-color;
    margin: 0;
    line-height: 1.4;
  }
}

.upsell-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid $wrapper--mds_brand_appearance_neutral_weak_border-color;

  .action-text {
    font-size: 13px;
    color: $wrapper--mds_brand_appearance_neutral_weak_text-color;
    margin: 0;
  }

  .action-email {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 20px;
    background-color: var(--mds_brand_appearance_primary_default_link-color);
    color: $wrapper--mds_brand_appearance_neutral_inverse_text-color;
    font-weight: 600;
    font-size: 13px;
    text-decoration: none;
    border-radius: 24px;
    transition:
      background-color 0.2s ease,
      transform 0.2s ease;

    &:hover {
      background-color: var(--mds_brand_appearance_interactive_hover_background-color, #00548f);
      transform: translateY(-1px);
    }

    .email-icon {
      width: 16px;
      height: 16px;
    }
    .email-icon::part(icon) {
      fill: $wrapper--mds_brand_appearance_neutral_inverse_text-color;
    }
  }
}
</style>
